import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { EloTier } from "../../../../../config/game/ranked"
import { getSessionToken } from "../../../network"
import "./tier-badge.css"

interface TierThresholds {
  [EloTier.MASTER_BALL]: number
  [EloTier.ULTRA_BALL]: number
  [EloTier.GREAT_BALL]: number
  [EloTier.POKE_BALL]: number
}

interface TierCache {
  thresholds: TierThresholds
  totalPlayers: number
  lastUpdated: string
}

const tierColors: Record<EloTier, string> = {
  [EloTier.MASTER_BALL]: "#9B59B6",
  [EloTier.ULTRA_BALL]: "#F1C40F",
  [EloTier.GREAT_BALL]: "#3498DB",
  [EloTier.POKE_BALL]: "#E74C3C"
}

const tierOrder = [EloTier.MASTER_BALL, EloTier.ULTRA_BALL, EloTier.GREAT_BALL, EloTier.POKE_BALL]

function getDisplayTier(elo: number, thresholds: TierThresholds): EloTier {
  if (elo >= thresholds[EloTier.MASTER_BALL]) return EloTier.MASTER_BALL
  if (elo >= thresholds[EloTier.ULTRA_BALL]) return EloTier.ULTRA_BALL
  if (elo >= thresholds[EloTier.GREAT_BALL]) return EloTier.GREAT_BALL
  return EloTier.POKE_BALL
}

export function TierBadge(props: { elo: number }) {
  const { t } = useTranslation()
  const [tierCache, setTierCache] = useState<TierCache | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchTierThresholds = async () => {
      try {
        const token = getSessionToken()
        const response = await fetch("/api/tier-thresholds", {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        })
        if (response.ok) {
          const data = await response.json()
          setTierCache(data)
        }
      } catch (e) {
        console.error("Failed to fetch tier thresholds:", e)
      } finally {
        setLoading(false)
      }
    }

    fetchTierThresholds()
  }, [])

  if (loading) {
    return null
  }

  const fallbackThresholds: TierThresholds = {
    [EloTier.MASTER_BALL]: 2000,
    [EloTier.ULTRA_BALL]: 1500,
    [EloTier.GREAT_BALL]: 1200,
    [EloTier.POKE_BALL]: 0
  }

  const tier = getDisplayTier(props.elo, tierCache?.thresholds ?? fallbackThresholds)
  const tierColor = tierColors[tier]

  return (
    <div className="tier-badge" style={{ borderColor: tierColor }}>
      <img
        src={`assets/ranks/${tier.toLowerCase().replace("_", "-")}.svg`}
        alt={t(`tier.${tier}`)}
        title={t(`tier.${tier}`)}
        onError={(e) => {
          // Fallback if image doesn't exist
          (e.target as HTMLImageElement).style.display = "none"
        }}
      />
      <span className="tier-name" style={{ color: tierColor }}>
        {t(`tier.${tier}`)} {t("league")}
      </span>
    </div>
  )
}
